import { config } from '@js/state';
import { SPACE_BG, addLaunchpad, roundBgKey } from '@utils/space';
import { PUMP_ORDER, PUMP_COLOR_CSS } from '@utils/roundGenerator';

/**
 * IntroScene — the three story screens (dev note: "First 3 screen appears, and
 * then the tutorial."). Layer order: background -> launchpad -> dim overlay ->
 * content. Screen 2 adds the 3 pumps; screen 3 adds the pumps + the % bar.
 * Tap anywhere (or the arrow) to advance; after the last screen, start the game.
 */
const SCREENS = [
  { text: 'Every rocket needs the perfect fuel ratio.', y: 200, pumps: false, bar: false },
  { text: 'Find the simplest ratio of the fuel in the three pumps.', y: 167, pumps: true, bar: false },
  { text: 'Each pump shows its fuel percentage.\nEnter the simplest whole-number ratio.', y: 110, pumps: true, bar: true },
];
const PUMP_X = [427, 640, 853]; // t2/t3 pump centres
const PUMP_Y = 458;
const TUT_PCT = [25, 25, 50];
const BAR = { cx: 640, cy: 227, w: 424, h: 53, border: 2 }; // t3 bar position
const NEXT_DELAY = 1500; // reading pause before the next arrow (and advancing) arms

export default class IntroScene extends Phaser.Scene {
  constructor() {
    super({ key: 'IntroScene' });
  }

  create() {
    const { width: W } = config;
    this.cameras.main.setBackgroundColor(SPACE_BG);
    this.cameras.main.fadeIn(300, 0, 0, 0);

    this.add.image(640, 360, roundBgKey(0)).setDepth(0);
    addLaunchpad(this);
    this.add.image(640, 368, 'overlay-dim').setDepth(2);

    this.pumps = PUMP_ORDER.map((c, i) =>
      this.add.image(PUMP_X[i], PUMP_Y, `pump-${c}`).setDepth(3).setVisible(false),
    );
    this.bar = this.buildStaticBar();
    this.arrow = this.buildArrow(1177, 618);

    this.label = this.add
      .text(W / 2, 200, '', {
        fontFamily: 'Athletics-Regular',
        fontSize: '34px',
        color: '#ffffff',
        align: 'center',
        lineSpacing: 10,
        wordWrap: { width: 1100 },
      })
      .setOrigin(0.5)
      .setDepth(4);

    this.index = 0;
    this.advancing = false;
    this.showScreen(0);

    this.input.on('pointerup', () => this.advance());
  }

  showScreen(i) {
    const s = SCREENS[i];
    this.label.setText(s.text).setY(s.y);
    this.pumps.forEach(p => p.setVisible(s.pumps));
    this.bar.setVisible(s.bar);
    // Deliberate pause: the next arrow appears (and tapping advances) only
    // after the kid has had time to read the screen.
    this.advancing = true;
    this.arrow.setVisible(false).setAlpha(0);
    this.time.delayedCall(NEXT_DELAY, () => {
      this.arrow.setVisible(true);
      this.tweens.add({ targets: this.arrow, alpha: 1, duration: 200 });
      this.advancing = false;
    });
  }

  // Static percentage bar (25/25/50) — same dark 536:1127 style as in-game.
  buildStaticBar() {
    const c = this.add.container(0, 0).setDepth(3).setVisible(false);
    const g = this.add.graphics();
    c.add(g);
    const left = BAR.cx - BAR.w / 2;
    const top = BAR.cy - BAR.h / 2;
    g.fillStyle(0x313131, 1);
    g.fillRect(left, top, BAR.w, BAR.h);
    g.lineStyle(2, 0xdadada, 1);
    g.strokeRect(left, top, BAR.w, BAR.h);
    const innerW = BAR.w - 2 * BAR.border;
    let segX = left + BAR.border;
    TUT_PCT.forEach((pct, i) => {
      const segW = (pct / 100) * innerW;
      const t = this.add
        .text(segX + segW / 2, BAR.cy, `${pct}%`, {
          fontFamily: 'Athletics-Bold',
          fontSize: '19px',
          color: PUMP_COLOR_CSS[PUMP_ORDER[i]],
        })
        .setOrigin(0.5);
      c.add(t);
      if (i < 2) {
        g.lineStyle(2, 0xdadada, 1);
        g.lineBetween(segX + segW, top + 2, segX + segW, top + BAR.h - 2);
      }
      segX += segW;
    });
    return c;
  }

  // Yellow "next" arrow (advance affordance). Tap anywhere advances; this
  // sprite also takes pointer hits and shows a hand cursor on hover.
  buildArrow(x, y) {
    const btn = this.add
      .image(x, y, 'next-button')
      .setOrigin(0.5)
      .setDepth(4)
      .setInteractive({ useHandCursor: true });
    // Hover = charcoal texture swap (Cuemath button-state convention), no scaling.
    btn.on('pointerover', () => btn.setTexture('next-button-hover'));
    btn.on('pointerout', () => btn.setTexture('next-button'));
    return btn;
  }

  advance() {
    // ─── Pre-flight ───
    // Inputs: pointerup anywhere. Re-entry guard: this.advancing blocks taps
    // during the fade between screens, the NEXT_DELAY reading pause (showScreen
    // re-arms it; the delayedCall releases it), and the final fade-out.
    // Cleanup: handler and timers die with the scene on scene.start.
    // Manually verified: [x] double tap [x] tap during fade [x] tap during reading pause [x] rapid taps
    if (this.advancing) return;
    this.advancing = true;
    this.index += 1;
    if (this.index >= SCREENS.length) {
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => this.scene.start('GameScene'));
      return;
    }
    this.tweens.add({
      targets: this.label,
      alpha: 0,
      duration: 160,
      onComplete: () => {
        this.showScreen(this.index);
        this.label.setAlpha(0);
        this.tweens.add({ targets: this.label, alpha: 1, duration: 200 });
      },
    });
  }
}
